import L from 'leaflet'
import 'mapbox-gl-leaflet/leaflet-mapbox-gl.js'
import config from '@/config'
import layersConfig from '@/lib/layersConfig'

function getIcon (name = 'generic') {
  return L.icon({
    iconUrl: `${process.env.BASE_URL}icons/${name.toLowerCase()}.svg`,
    iconSize: [40, 40],
    iconAnchor: [30, 20],
    popupAnchor: [-3, -76]
  })
}

function getScaledIcon (name, zoom) {
  const iconName = `${name.toLowerCase()}.svg`
  const getLength = z => {
    return Math.round(z * 15)
  }
  const iconSize = [getLength(zoom), getLength(zoom)]
  return L.icon({
    iconUrl: `${process.env.BASE_URL}icons/${iconName}`,
    iconSize,
    iconAnchor: [30, 20],
    popupAnchor: [-3, -76]
  })
}

export default class LeafletMap {
  constructor (htmlElement, features, vueStore, isIconGeneric) {
    this.isIconGeneric = isIconGeneric
    this.features = features
    const mapConfig = config.map
    this.store = vueStore
    this.map = L.map(htmlElement).setView(mapConfig.lnglat, mapConfig.zoom)
    this.map.attributionControl.addAttribution('&copy; <a href="http://www.anagraph.io"> Anagraph </a>© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>')
    this.layers = {}
    this.addStaticLayers(['iceEdge', 'hunt'])
    this.layers.eventLayer = L.layerGroup()
    this.layers.highlightLayer = L.layerGroup()
    // L.tileLayer(
    //   mapConfig.tileLayer.url,
    //   mapConfig.tileLayer.config
    // ).addTo(this.map)
    L.mapboxGL({
      accessToken: 'pk.eyJ1IjoiY3ZtdGwiLCJhIjoiY2l2ZWEwNzBkMDBwbzJvbzB0ZWdjM2JiaSJ9.QZAmBwV1f-EYQ20GnZkJ8A',
      style: 'mapbox://styles/isuma/cjuaqh4fd1efl1fo3jfpdsiep'
    }).addTo(this.map)
    this.map.on('load', () => {
      this.updateEventLayerTooltips()
    })
  }
  updateIconSizes (zoom) {
    const iconsLayer = this.layers.hunt.getLayers()
    iconsLayer.map(layer => {
      layer.remove()
      if (zoom >= 8) {
        layer.setIcon(getScaledIcon(layer.feature.properties.icon || layer.feature.properties.Icon || 'generic', zoom))
        layer.addTo(this.map)
      }
    })
  }
  addStaticLayers (layerIds) {
    layerIds.map(layerId => {
      const config = layersConfig[layerId]
      const features = this.features.filter(config.filter)
      const options = config.options(this.map.getZoom())
      this.layers[layerId] = L.geoJSON(features, options).addTo(this.map)
    })
  }
  focusOnMarker (selectedId) {
    this.layers.eventLayer.getLayers().map(layer => {
      if (layer.feature.properties.geometry_id !== selectedId) {
        layer._icon.style.opacity = 0.3
        if (layer.getTooltip()) layer.getTooltip()._container.style.opacity = 0.3
      } else {
        layer._icon.style.opacity = 1
        if (layer.getTooltip()) layer.getTooltip()._container.style.opacity = 1
      }
    })
  }
  loadEventLayer (features) {
    const markerDefault = this.isIconGeneric ? 'generic' : false
    const store = this.store
    const type = this.map.getZoom() > 5 ? 'marker' : 'point'
    this.layers.eventLayer.remove()
    global.eventLayer = this.layers.eventLayer
    this.layers.eventLayer = L.geoJSON(features, {
      pointToLayer: (feature, latLng) => {
        const nameIn = feature.properties['Name IN'] || ''
        const nameRn = feature.properties['Name RN'] || ''
        const iconName = type === 'marker'
          ? markerDefault || feature.properties.icon || feature.properties.Icon || 'generic'
          : 'far'
        const marker = new L.Marker(latLng, { icon: getIcon(iconName) })
          .on('click', (e) => {
            L.DomEvent.stopPropagation(event)
            if (!store.state.isMobile) {
              this.focusOnMarker(feature.properties.geometry_id)
            }
            this.store.commit('setSelectedGeometry', {
              id: feature.properties['Scenario ID'] || feature.properties.geometry_id,
              nameIn,
              nameRn,
              description: feature.properties['Description EN']
            })
            this.store.commit('setModalOpened', true)

            const viewPortPosition = this.map.latLngToLayerPoint(e.latlng)
            const viewPortCenter = new L.Point(viewPortPosition.x - 360, viewPortPosition.y)
            if (!store.state.isMobile) {
              this.map.flyTo(this.map.layerPointToLatLng(viewPortCenter))
            }
          })
        return marker
      }
    }).addTo(this.map)
  }
  loadHighlightLayer (features) {
    this.layers.highlightLayer.remove()
    this.layers.highlightLayer = L.geoJSON(features, layersConfig.highlight.options()).addTo(this.map)
  }
  getLayerPoint (layer) {
    return this.map.latLngToLayerPoint(layer.getLatLng())
  }
  updateEventLayerTooltips () {
    this.layers.eventLayer.eachLayer(layer => {
      const nameIn = layer.feature.properties['Name IN'] || ''
      const nameRn = layer.feature.properties['Name RN'] || ''
      const layerPoint = this.map.latLngToLayerPoint(layer.getLatLng())
      const layersAround = this.layers.eventLayer.getLayers().filter(candidateLayer => {
        const candidateLayerPoint = this.map.latLngToLayerPoint(candidateLayer.getLatLng())
        const distanceX = candidateLayerPoint.x - layerPoint.x
        const distanceY = Math.abs(candidateLayerPoint.y - layerPoint.y)
        const xTooClose = distanceX < 10 && distanceX > -25
        return xTooClose && distanceY < 50
      })
      if (layersAround.length === 1 && nameIn.length < 20 && nameRn.length < 20) {
        layer.bindTooltip(
          `<div class="poi-labels lang-in"><div class="poi-label">${nameIn}</div><div class="poi-label lang-en">${nameRn}</div></div>`,
          { permanent: true, direction: 'bottom', offset: new L.Point(-10, 10) }
        )
      } else {
        layer.unbindTooltip()
      }
    })
  }
}
